import React, { useCallback, useEffect, useState } from 'react';
import { Agente, Empresa, Sitio } from '../types';
import { getAgentes, createAgente, updateAgente, deleteAgente } from '../services/agentes.service';
import { getSitios } from '../services/sitios.service';
import { asignarAgenteASitio, desasignarAgenteDeSitio, getSitiosByAgente } from '../services/relaciones.service';
import { getEmpresas } from '../services/empresas.service';
import { getErrorMessage } from '../utils/errorHandler';
import { getCurrentUserRole } from '../utils/auth';

interface ModalProps {
  agente?: Agente | null;
  isAdmin: boolean;
  empresas: Empresa[];
  sitios: Sitio[];
  onClose: () => void;
  onSave: () => void;
}

const AgenteModal: React.FC<ModalProps> = ({ agente, isAdmin, empresas, sitios, onClose, onSave }) => {
  const [nombre, setNombre] = useState(agente?.nombre || '');
  const [dni, setDni] = useState((agente as any)?.documento || agente?.dni || '');
  const [idAgente, setIdAgente] = useState(agente?.idAgente || '');
  const [telefono, setTelefono] = useState(agente?.telefono || '');
  const [empresaId, setEmpresaId] = useState(agente?.empresaId || '');
  const [activo, setActivo] = useState(agente?.activo ?? true);
  const [sitiosSelected, setSitiosSelected] = useState<string[]>(
    agente?.sitios
      ? (agente.sitios as any[]).map((s: any) => (typeof s === 'string' ? s : s._id))
      : []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const cargarSitiosAsignados = async () => {
      if (!agente?._id) return;
      try {
        const resp = await getSitiosByAgente(agente._id);
        const assigned = (resp.data || [])
          .map((rel: any) => {
            const sitio = rel?.sitioId;
            return typeof sitio === 'string' ? sitio : sitio?._id;
          })
          .filter((id: string | undefined): id is string => Boolean(id));
        setSitiosSelected(assigned);
      } catch {
        setSitiosSelected(
          agente?.sitios
            ? (agente.sitios as any[]).map((s: any) => (typeof s === 'string' ? s : s._id))
            : [],
        );
      }
    };

    setNombre(agente?.nombre || '');
    setDni((agente as any)?.documento || agente?.dni || '');
    setIdAgente(agente?.idAgente || '');
    setTelefono(agente?.telefono || '');
    setEmpresaId(agente?.empresaId || '');
    setActivo(agente?.activo ?? true);

    if (!agente?._id) {
      setSitiosSelected([]);
      return;
    }

    void cargarSitiosAsignados();
  }, [agente]);

  useEffect(() => {
    if (!empresaId && isAdmin && empresas.length === 1) {
      setEmpresaId(empresas[0]._id);
    }
  }, [empresaId, isAdmin, empresas]);

  const handleSelectSitio = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const opts = Array.from(e.target.selectedOptions).map(o => o.value);
    setSitiosSelected(opts);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      let agenteId = agente?._id || '';

      if (agente?._id) {
        const updateResp = await updateAgente(agente._id, {
          nombre,
          documento: dni,
          telefono,
          empresaId: isAdmin ? (empresaId || undefined) : undefined,
          activo,
        } as any);
        agenteId = updateResp.data?._id || agente._id;
      } else {
        if (isAdmin && !empresaId) {
          setError('Seleccione una empresa para crear el agente.');
          setSaving(false);
          return;
        }

        const createResp = await createAgente({
          nombre,
          documento: dni,
          telefono,
          empresaId: isAdmin ? empresaId : undefined,
        } as any);
        agenteId = createResp.data?._id || '';
      }

      if (agenteId) {
        const relResp = await getSitiosByAgente(agenteId);
        const currentIds = (relResp.data || [])
          .map((rel: any) => {
            const sitio = rel?.sitioId;
            return typeof sitio === 'string' ? sitio : sitio?._id;
          })
          .filter((id: string | undefined): id is string => Boolean(id));

        const toAssign = sitiosSelected.filter((id) => !currentIds.includes(id));
        const toUnassign = currentIds.filter((id) => !sitiosSelected.includes(id));

        if (toAssign.length > 0) {
          await Promise.all(toAssign.map((sitioId) => asignarAgenteASitio(agenteId, sitioId)));
        }

        if (toUnassign.length > 0) {
          await Promise.all(toUnassign.map((sitioId) => desasignarAgenteDeSitio(agenteId, sitioId)));
        }
      }
      onSave();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{agente ? 'Editar agente' : 'Nuevo agente'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {isAdmin && (
              <div className="form-group">
                <label>Empresa</label>
                <select value={empresaId} onChange={e => setEmpresaId(e.target.value)} required>
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(emp => (
                    <option key={emp._id} value={emp._id}>{emp.razonSocial}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Nombre</label>
              <input value={nombre} onChange={e => setNombre(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>DNI</label>
              <input value={dni} onChange={e => setDni(e.target.value)} placeholder="12345678" required />
            </div>
            <div className="form-group">
              <label>ID_Agente</label>
              <input value={idAgente} onChange={e => setIdAgente(e.target.value)} placeholder="AGT-001" />
            </div>
            <div className="form-group">
              <label>Telefono WhatsApp</label>
              <input value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="+54911..." required />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '8px' }}>
            <label>Sitios asignados</label>
            <select multiple value={sitiosSelected} onChange={handleSelectSitio}>
              {sitios.map(s => (
                <option key={s._id} value={s._id}>{s.nombre}</option>
              ))}
            </select>
          </div>
          <p className="form-hint">Mantén presionado Ctrl (o Cmd) para seleccionar varios sitios.</p>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="agenteActivo"
              checked={activo}
              onChange={e => setActivo(e.target.checked)}
            />
            <label htmlFor="agenteActivo">Agente activo</label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : agente ? 'Guardar cambios' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PAGE_SIZE = 30;

const AgentesPage: React.FC = () => {
  const role = getCurrentUserRole();
  const isAdmin = role === 'ADMIN';
  const [agentes, setAgentes] = useState<Agente[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [sitios, setSitios] = useState<Sitio[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState<Agente | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(busqueda.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [busqueda]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, e] = await Promise.all([
        getAgentes({ page, limit: PAGE_SIZE, search: search || undefined }),
        getSitios(),
        isAdmin ? getEmpresas() : Promise.resolve({ data: [] as Empresa[] }),
      ]);
      setSitios(s.data);
      setEmpresas(e.data);
      setTotal(a.total);

      // /agentes does not include relationships; enrich each agente with assigned sitios.
      const relaciones = await Promise.allSettled(
        a.data.map((agente) => getSitiosByAgente(agente._id)),
      );

      const agentesConSitios = a.data.map((agente, idx) => {
        const rel = relaciones[idx];
        if (rel.status !== 'fulfilled') {
          return { ...agente, sitios: [] };
        }

        const sitiosAsignados = (rel.value.data || [])
          .map((r: any) => r?.sitioId)
          .filter((sitio: unknown) => Boolean(sitio));

        return {
          ...agente,
          sitios: sitiosAsignados,
        };
      });

      setAgentes(agentesConSitios);
    } catch {}
    setLoading(false);
  }, [isAdmin, page, search]);

  useEffect(() => { cargar(); }, [cargar]);

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPaginas) setPage(totalPaginas);
  }, [page, totalPaginas]);

  const handleEliminar = async (id: string) => {
    if (!window.confirm('¿Eliminar este agente?')) return;
    try {
      await deleteAgente(id);
      cargar();
    } catch {}
  };

  const getSitioNombre = (sId: any) => {
    if (typeof sId === 'object') return sId.nombre;
    const s = sitios.find(x => x._id === sId);
    return s?.nombre || sId;
  };

  return (
    <>
      {showModal && (
        <AgenteModal
          agente={editando}
          isAdmin={isAdmin}
          empresas={empresas}
          sitios={sitios}
          onClose={() => { setShowModal(false); setEditando(null); }}
          onSave={() => { setShowModal(false); setEditando(null); cargar(); }}
        />
      )}

      <h1 className="page-title">Geo Asistencia - Legajos</h1>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 className="card-title" style={{ marginBottom: 0 }}>Nuevo colaborador</h2>
          <button className="btn btn-primary" onClick={() => { setEditando(null); setShowModal(true); }}>
            + Nuevo colaborador
          </button>
        </div>
      </div>

      <div className="card">
        <h2 className="card-title">Listado de Colaboradores</h2>
        <div className="form-group" style={{ maxWidth: '360px', marginBottom: '12px' }}>
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre o DNI..."
          />
        </div>
        {!loading && (
          <p className="form-hint" style={{ marginBottom: '12px' }}>
            Total empleados: {total}{search ? ' (filtrados)' : ''} · Página {page} de {totalPaginas}
          </p>
        )}
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : agentes.length === 0 ? (
          <div className="empty-state">
            {search ? 'No hay colaboradores que coincidan con la búsqueda.' : 'No hay colaboradores registrados.'}
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>DNI</th>
                  <th>ID Colaborador</th>
                  <th>WhatsApp</th>
                  <th>Sitios asignados</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {agentes.map((a, index) => (
                  <tr key={a._id}>
                    <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                    <td>{a.nombre}</td>
                    <td>{(a as any).documento || a.dni || '-'}</td>
                    <td>{a.idAgente || '-'}</td>
                    <td>{a.telefono}</td>
                    <td>
                      {a.sitios?.length
                        ? (a.sitios as any[]).map((s: any, i: number) => (
                          <span key={i}>
                            {i > 0 ? ' | ' : ''}
                            <span style={{ color: '#3498db' }}>{getSitioNombre(s)}</span>
                          </span>
                        ))
                        : '-'}
                    </td>
                    <td>
                      <span className={`badge ${a.activo ? 'badge-success' : 'badge-secondary'}`}>
                        {a.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button className="btn btn-outline btn-sm" onClick={() => { setEditando(a); setShowModal(true); }}>
                          Editar
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleEliminar(a._id)}>
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && totalPaginas > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Anterior
            </button>
            <span className="form-hint">Página {page} de {totalPaginas}</span>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
              disabled={page >= totalPaginas}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AgentesPage;
